import { ApolloError } from "@apollo/client";
import { OpenInNew } from "@mui/icons-material";
import {
  Typography,
  Box,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { ReactElement } from "react";

import {
  ImportOptOutsResults,
  OptOutsInListQuery,
} from "~frontend/__generated__/graphql";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import { plural } from "~frontend/util/plural";
import { spacingArray } from "~frontend/util/styling";

import { AccentBox } from "../AccentBox";
import CollapsibleDrawer from "../CollapsibleDrawer";
import ErrorCard from "../ErrorCard";
import { FlexBox } from "../FlexBox";
import { InternalLink } from "../Link";
import Loading from "../Loading";
import { TwoColumnDisplay, LightDivider } from "../TwoColumnDisplay";
import { List } from "./OptOutListTable";

const TotalOptOuts = ({
  count,
  result,
  fileName,
}: {
  count: number;
  result?: ImportOptOutsResults | null;
  fileName?: string;
}): ReactElement => {
  return (
    <CollapsibleDrawer
      variant="slim"
      title={count.toLocaleString()}
      expandText="Import Summary"
      startOpen={false}
      width="100%"
    >
      <FlexBox direction="column" gap={1} sx={{ pt: 1 }}>
        {result?.nUploaded ? (
          <Typography variant="body2">
            {plural(result.nUploaded, "row", "rows")} uploaded from{" "}
            {fileName || "unknown file"}
          </Typography>
        ) : null}
        {result?.nInvalid ? (
          <Typography variant="body2">
            {plural(result.nInvalid, "row", "rows")} skipped due to invalid
            phone numbers
          </Typography>
        ) : null}
        {result?.nDuplicates ? (
          <Typography variant="body2">
            {plural(result.nDuplicates, "duplicate", "duplicates")} skipped
          </Typography>
        ) : null}
        {result?.nAlreadyOptedOut ? (
          <Typography variant="body2">
            {plural(result.nAlreadyOptedOut, "row", "rows")} skipped because
            they are already opted out
          </Typography>
        ) : null}

        <Typography variant="body2Bold">
          {plural(count, "contact", "contacts")} opted out
        </Typography>
      </FlexBox>
    </CollapsibleDrawer>
  );
};

export const OptOutListDetail = ({
  listDetails,
  listDetailsLoading,
  listDetailsError,
  optOuts,
  onRequestNextPage,
  nextPageLoading,
  onShowNumberDetails,
}: {
  listDetails?: List | null;
  listDetailsLoading: boolean;
  listDetailsError?: ApolloError | null;
  optOuts: OptOutsInListQuery["optOutsInList"];
  onRequestNextPage: () => Promise<void>;
  nextPageLoading: boolean;
  onShowNumberDetails: (phoneNumber: string) => void;
}): ReactElement => {
  const subaccountContext = useSubaccount();

  if (listDetailsLoading) {
    return <Loading />;
  }

  if (listDetailsError) {
    return <ErrorCard errors={[listDetailsError]} />;
  }

  if (!listDetails) {
    return <Loading />;
  }

  return (
    <AccentBox sx={{ gap: theme.spacing(2) }}>
      <Typography variant="h1">
        <strong>{listDetails.name}</strong> Details
      </Typography>

      <TwoColumnDisplay
        rows={[
          listDetails.subaccount &&
            listDetails.subaccount.id !== subaccountContext?.subaccount.id && {
              key: "subaccount",
              label: "Subaccount",
              content: (
                <InternalLink
                  to={`/subaccounts/${listDetails.subaccount.slug}/opt-outs`}
                >
                  {listDetails.subaccount.name}
                </InternalLink>
              ),
            },
          {
            key: "list-type",
            label: "List Type",
            content: listDetails.expiresAt
              ? "Temporary List"
              : "Permanent List",
          },
          {
            key: "added-by",
            label: "Added By",
            content: listDetails.createdByUser?.fullName,
          },
          {
            key: "added-at",
            label: "Added At",
            content: new Date(listDetails.createdAt).toLocaleString(),
          },
          listDetails.expiresAt && {
            key: "expires-at",
            label: "Expires At",
            content: new Date(listDetails.expiresAt).toLocaleString(),
          },
          listDetails.deletedAt && {
            key: "deleted-at",
            label: "Deleted At",
            content: new Date(listDetails.deletedAt).toLocaleString(),
          },
          {
            key: "opt-out-count",
            label: "Total Opt Outs",
            labelSx: {
              display: "flex",
              alignContent: "flex-start",
              flexWrap: "unset",
            },
            alignItems: "flex-start",
            content: (
              <TotalOptOuts
                count={listDetails.count}
                result={listDetails.importJob?.result}
                fileName={listDetails.upload?.fileName}
              />
            ),
          },
        ].filter(row => !!row)}
      />

      <Box
        sx={{
          backgroundColor: palette.lightestGray,
          padding: spacingArray([3, 5]),
          height: 400,
          overflow: "scroll",
        }}
      >
        <FlexBox flexDirection="column">
          {optOuts.map((optOut, i) => (
            <FlexBox flexDirection="column" key={optOut.id}>
              <FlexBox justifyContent="space-between" alignItems="center">
                <Typography variant="body1">
                  {prettyPhoneNumber(optOut.phoneNumber)}
                </Typography>
                <IconButton
                  onClick={() => onShowNumberDetails(optOut.phoneNumber)}
                >
                  <OpenInNew />
                </IconButton>
              </FlexBox>
              {i + 1 < optOuts.length && <LightDivider />}
            </FlexBox>
          ))}

          {optOuts.length < listDetails.count && (
            <Button disabled={nextPageLoading} onClick={onRequestNextPage}>
              {nextPageLoading ? <CircularProgress /> : "Load More"}
            </Button>
          )}
        </FlexBox>
      </Box>
    </AccentBox>
  );
};

export const OptOutListDeleteButton = ({
  listDetails,
  isDeleting,
  onClick,
}: {
  listDetails: List;
  isDeleting: boolean;
  onClick: () => any;
}): ReactElement => {
  if (
    listDetails.deletedAt ||
    (listDetails.expiresAt && listDetails.expiresAt < new Date())
  ) {
    return <></>;
  }

  return (
    <Button
      disabled={isDeleting}
      variant="outlined"
      color="error"
      onClick={onClick}
    >
      {isDeleting ? "Deleting..." : "Delete List"}
    </Button>
  );
};
